import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, LogOut, Lock } from "lucide-react";
import { useAuth, api } from "../../context/AuthContext";
import { InventoryPanel } from "./InventoryPanel";
import { OrdersPanel } from "./OrdersPanel";
import { FlagOrdersPanel } from "./FlagOrdersPanel";

const TABS = [
  { id: "inventory", label: "Inventory" },
  { id: "orders", label: "Orders" },
  { id: "flags", label: "Flag requests" },
];

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("inventory");
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setCatalog(data.products));
  }, []);

  if (user === null) {
    return (
      <div
        data-testid="admin-loading"
        className="min-h-screen flex items-center justify-center"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mutedink">
          Checking session…
        </p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex flex-col">
      <header className="border-b border-ink/10 px-6 py-4 bg-[#F9F8F5]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-2 text-sm text-ink hover:text-oxblood transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Site
          </Link>
          <span className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-ink">
            HAILEY <span className="text-bronze">&amp;</span> CHLOE — ADMIN
          </span>
          <div className="flex items-center gap-4">
            <Link
              to="/hyrox-flags"
              data-testid="admin-flags-page-link"
              className="inline-flex items-center gap-1.5 text-sm text-ink hover:text-oxblood transition-colors duration-300"
              title="Private page — only visible when signed in"
            >
              <Lock className="w-3.5 h-3.5" />
              Hyrox Flags page
            </Link>
          <button
            type="button"
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-ink hover:text-oxblood transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink mb-3">
            Signed in as {user.email}
          </p>
          <h1 className="font-serif font-light tracking-tight text-ink text-3xl sm:text-4xl">
            Back office
          </h1>

          <div
            className="mt-8 mb-8 flex gap-2 border-b border-ink/10"
            role="tablist"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
                  tab === t.id
                    ? "border-oxblood text-oxblood"
                    : "border-transparent text-mutedink hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "inventory" && catalog && (
            <InventoryPanel
              catalog={catalog}
              onPriceChange={(id, price) =>
                setCatalog((c) => ({ ...c, [id]: { ...c[id], price } }))
              }
            />
          )}
          {tab === "orders" && <OrdersPanel />}
          {tab === "flags" && <FlagOrdersPanel />}
        </div>
      </main>
    </div>
  );
};
